import { useState, useEffect } from "react";
import { useParams, Routes, Route, useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { 
  Users, 
  TrendingUp, 
  BookOpen, 
  Lightbulb, 
  Trophy,
  LayoutDashboard,
  LogOut,
  Settings,
  Sparkles,
  UserPlus,
  Command
} from "lucide-react";
import Dashboard from "@/components/Dashboard";
import AccountsView from "@/components/AccountsView";
import PipelineView from "@/components/PipelineView";
import ContentView from "@/components/ContentView";
import AdvisorView from "@/components/AdvisorView";
import GoalsView from "@/components/GoalsView";
import TeamView from "@/components/TeamView";
import OnboardingModal from "@/components/OnboardingModal";
import SettingsModal from "@/components/SettingsModal";

const CommandModule = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Check localStorage first for faster initial render for returning users
  const initialOnboardingState = () => {
    if (userId === "new") return true;
    // Check both the new per-user key and auth status
    const stored = localStorage.getItem(`onboardingComplete_${userId}`);
    const isAuth = localStorage.getItem("isAuthenticated") === "true";
    
    // If user is authenticated with a real userId (not 'new'), don't show onboarding
    // This is a failsafe - authenticated users with real IDs should never see onboarding
    if (isAuth && userId && userId !== "new") {
      console.log(`[Onboarding] Authenticated user ${userId} - skipping onboarding`);
      return false;
    }
    
    if (stored === "true") {
      console.log(`[Onboarding] Initial check for ${userId}: localStorage value = ${stored}`);
      return false;
    }
    return null; // null = need to check API
  };
  
  const [showOnboarding, setShowOnboarding] = useState(initialOnboardingState);
  const [showSettings, setShowSettings] = useState(false);
  const [userData, setUserData] = useState(null);
  const [isNewUser, setIsNewUser] = useState(userId === "new");
  
  // Debug log whenever showOnboarding changes
  useEffect(() => {
    console.log(`[Onboarding] showOnboarding state changed to: ${showOnboarding}`);
  }, [showOnboarding]);

  // Fetch user data and check if new user
  useEffect(() => {
    if (userId === "new") {
      setIsNewUser(true);
      setShowOnboarding(true);
    } else if (showOnboarding === null) {
      // Only check API if we don't have cached status
      const fetchUserData = async () => {
        try {
          // First check auth status to see if onboarding is complete
          const authCheck = await axios.get(`${API}/auth/check/${userId}`);
          
          if (authCheck.data.onboardingComplete && authCheck.data.hasProfile) {
            // User has completed onboarding, get their profile
            const response = await axios.get(`${API}/users/${userId}`);
            setUserData(response.data);
            setIsNewUser(false);
            setShowOnboarding(false);
            // Store onboarding complete status for faster future checks
            localStorage.setItem(`onboardingComplete_${userId}`, "true");
          } else {
            // User hasn't completed onboarding
            setIsNewUser(true);
            setShowOnboarding(true);
          }
        } catch (error) {
          console.error("Error fetching user data:", error);
          // If profile not found, show onboarding
          if (error.response?.status === 404) {
            setIsNewUser(true);
            setShowOnboarding(true);
          } else {
            // On other errors, don't block the UI - assume onboarding is complete if we have a real userId
            setShowOnboarding(false);
          }
        }
      };
      fetchUserData();
    } else if (showOnboarding === false) {
      // Returning user, just fetch profile for display
      const fetchProfile = async () => {
        try {
          const response = await axios.get(`${API}/users/${userId}`);
          setUserData(response.data);
        } catch (error) {
          console.error("Error fetching profile:", error);
        }
      };
      fetchProfile();
    }
  }, [userId, showOnboarding]);

  const handleSetupClick = () => {
    if (isNewUser || userId === "new") {
      setShowOnboarding(true);
    } else {
      setShowSettings(true);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("isAuthenticated");
    localStorage.removeItem("username");
    localStorage.removeItem("userId");
    navigate("/");
  };

  const handleOnboardingComplete = async (newUserId) => {
    setShowOnboarding(false);
    setIsNewUser(false);
    
    // Store completion status for this user
    const effectiveUserId = newUserId || userId;
    localStorage.setItem(`onboardingComplete_${effectiveUserId}`, "true");
    
    // Update the userId in localStorage if it changed
    if (newUserId && newUserId !== userId) {
      localStorage.setItem("userId", newUserId);
      navigate(`/dashboard/${newUserId}`);
    }
    
    // Fetch the user profile
    if (effectiveUserId) {
      try {
        const res = await axios.get(`${API}/users/${effectiveUserId}`);
        setUserData(res.data);
      } catch (error) {
        console.error("Error fetching user profile:", error);
      }
    }
  };

  const handleSettingsClose = () => {
    setShowSettings(false);
    if (userId && userId !== "new") {
      axios.get(`${API}/users/${userId}`).then(res => setUserData(res.data));
    }
  };

  const navigationItems = [
    { label: "Command", path: "", icon: LayoutDashboard, color: "text-neon-cyan" },
    { label: "Accounts", path: "accounts", icon: Users, color: "text-neon-cyan" },
    { label: "Pipeline", path: "pipeline", icon: TrendingUp, color: "text-neon-cyan" },
    { label: "Content", path: "content", icon: BookOpen, color: "text-neon-cyan" },
    { label: "Advisor", path: "advisor", icon: Lightbulb, color: "text-neon-cyan" },
    { label: "Goals", path: "goals", icon: Trophy, color: "text-neon-cyan" },
    { label: "Team", path: "team", icon: UserPlus, color: "text-neon-cyan" }
  ];

  const currentPath = location.pathname.split('/').pop() || '';

  return (
    <div className="min-h-screen gradient-bg relative overflow-hidden">
      {/* Background Elements */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        {/* Cyber Grid */}
        <div className="absolute inset-0 cyber-grid opacity-30" />
      </div>

      {/* Header with Navigation */}
      <header className="border-b border-white/5 bg-black/80 sticky top-0 z-50">
        <div className="container mx-auto px-6 py-3">
          <div className="flex items-center justify-between">
            {/* Logo & Nav */}
            <div className="flex items-center gap-6">
              {/* Logo */}
              <motion.div 
                className="flex items-center gap-2 mr-4"
                whileHover={{ scale: 1.02 }}
              >
                <motion.div 
                  className="p-2 rounded-xl bg-gradient-to-br from-neon-cyan/20 to-neon-pink/10 border border-neon-cyan/20"
                  animate={{ 
                    boxShadow: [
                      "0 0 10px rgba(0,240,255,0.2)",
                      "0 0 20px rgba(0,240,255,0.3)",
                      "0 0 10px rgba(0,240,255,0.2)"
                    ]
                  }}
                  transition={{ duration: 2, repeat: Infinity }}
                >
                  <Command className="h-5 w-5 text-neon-cyan" />
                </motion.div>
                <span className="font-bold text-lg neon-text tracking-tight hidden lg:block">ISM</span>
              </motion.div>

              {/* Navigation */}
              {navigationItems.map((item, idx) => {
                const Icon = item.icon;
                const isActive = currentPath === item.path || (currentPath === '' && item.path === '');
                return (
                  <motion.div
                    key={item.path}
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: idx * 0.05 }}
                  >
                      <Button
                        onClick={() => navigate(`/dashboard/${userId}/${item.path}`)}
                        variant="ghost"
                        className={`relative px-4 py-2 rounded-xl transition-all ${
                          isActive
                            ? 'bg-neon-cyan/10 border border-neon-cyan/30 text-neon-cyan'
                            : 'border border-transparent text-gray-400 hover:text-white hover:bg-white/5'
                        }`}
                        data-testid={`nav-${item.label.toLowerCase()}`}
                      >
                      <Icon className={`h-4 w-4 mr-2 ${isActive ? item.color : 'text-gray-500'}`} />
                      <span className="hidden md:inline">{item.label}</span>
                      {isActive && (
                        <motion.div 
                          className="absolute -bottom-[13px] left-1/2 -translate-x-1/2 w-8 h-0.5 bg-neon-cyan rounded-full"
                          layoutId="activeTab"
                        />
                      )}
                    </Button>
                  </motion.div>
                );
              })}
            </div>

            {/* Right side buttons */}
            <div className="flex items-center gap-3">
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Button
                  onClick={handleSetupClick}
                  variant="ghost"
                  className="border border-white/10 text-gray-400 hover:text-white hover:bg-white/5 rounded-xl"
                  title={isNewUser ? "Setup New Sales Machine" : "Targeting Settings"}
                >
                  {isNewUser ? (
                    <>
                      <Sparkles className="h-4 w-4 mr-2 text-neon-cyan" />
                      <span className="hidden sm:inline">Setup</span>
                    </>
                  ) : (
                    <>
                      <Settings className="h-4 w-4 mr-2 text-neon-cyan" />
                      <span className="hidden sm:inline">Settings</span>
                    </>
                  )}
                </Button>
              </motion.div>
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Button
                  onClick={handleLogout}
                  variant="ghost"
                  className="border border-white/10 text-gray-400 hover:text-white hover:bg-white/5 rounded-xl"
                >
                  <LogOut className="h-4 w-4 mr-2" />
                  <span className="hidden sm:inline">Logout</span>
                </Button>
              </motion.div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-6 py-8 relative z-10">
        <Routes>
          <Route index element={<Dashboard />} />
          <Route path="accounts" element={<AccountsView />} />
          <Route path="pipeline" element={<PipelineView />} />
          <Route path="content" element={<ContentView />} />
          <Route path="advisor" element={<AdvisorView />} />
          <Route path="goals" element={<GoalsView />} />
          <Route path="team" element={<TeamView />} />
        </Routes>
      </main>

      {/* Footer */}
      <footer className="relative z-10 text-center py-8">
        <p className="text-xs text-gray-600">
          © Copyright 2024 <span className="text-gray-500">Intelligent Sales Machines, Inc.</span> – All rights reserved.
        </p>
      </footer>

      {/* Onboarding Modal - For new users or when Setup is clicked */}
      {(userId === "new" || showOnboarding) && (
        <OnboardingModal
          isOpen={userId === "new" || showOnboarding}
          onClose={() => setShowOnboarding(false)}
          onComplete={handleOnboardingComplete}
        />
      )}
      
      {/* Settings Modal (for existing users) */}
      <SettingsModal
        isOpen={showSettings}
        onClose={handleSettingsClose}
        userId={userId}
        initialData={userData}
      />
    </div>
  );
};

export default CommandModule;
